import { I18nService } from './i18n.service';
import { Injectable, OnDestroy } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { first, map, tap, switchMap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { AssetsService } from './assets.service';
import { DocumentTypesService } from './document-types.service';
import { InitiativesService } from './initiatives.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { SpreadsheetColumnsService } from './spreadsheet-columns.service';
import { RoadmapService } from './roadmap.service';
import { ColDef, ColumnGroupShowType } from 'ag-grid-community';
import { ExportsService } from './exports.service';
import { InitiativeTagsService } from './initiative-tags.service';
import { SeeButtonRendererComponent } from '../../@theme/components';
import { RightPanelDialogService } from './right-panel-dialog.service';
import { InitiativeFormComponent } from '../../@theme/components/side-forms/initiative-form/initiative-form.component';
import { QuotePartService } from './quote-part.service';
import { ConfigService } from './config.service';
import { QuantityUnitsService } from './quantity-units.service';

@Injectable()
export class SpreadsheetColumnsInitiativesService extends SpreadsheetColumnsService implements OnDestroy {

  /**
 * store the value of options that're going to fetch with the investment table so that we can
 * access this from other services.
 * @private
 */
  private readonly currentInitiativeTableOptions = new BehaviorSubject<KPI.TServerSideGetRowsRequest>(null);

  private quantityUnitMap = {};
  private investmentCodeFieldName: string;

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    private i18n: I18nService,
    private exportsService: ExportsService,
    private initiativeTagsService: InitiativeTagsService,
    private rightPanelDialogService: RightPanelDialogService,
    protected quotePartService: QuotePartService,
    protected config: ConfigService,
    private quantityUnitsService: QuantityUnitsService,
  ) {
    super(
      documentTypesService, investmentCategoriesService, investmentReasonsService,
      scope, translate, api, perimetersService, referenceDataService, assetsService,
      yearsService, route, investmentScenariiService, initiativesService, roadmapService,
      quotePartService, config
    );

    this.subscriptions.push(
      this.config.customerConfig$.subscribe((config) => {
        if (config?.investment_code_field_name) {
          this.investmentCodeFieldName = config.investment_code_field_name;
        } else {
          this.investmentCodeFieldName = this.translate.instant('Customize code');
        }
      })
    );
  }

  /**
   * Grays out a "Desired gains" cell when the initiative's instance for the active
   * scenario (forcedScenario, falling back to mainScenario) has stopped_trajectory_effects.
   */
  private stoppedTrajectoryCellClass(
    forcedScenario: KPI.InvestmentScenario,
    mainScenario: KPI.InvestmentScenario,
  ) {
    const scenarioId = (forcedScenario || mainScenario).id;
    return (params) =>
      params.data?.[`stopped_trajectory_effects_scenario_${scenarioId}`]
        ? ['cell-stopped-trajectory']
        : [];
  }

  /**
   * INITIATIVES TABLE COLUMNS DEF
   */
  private agGridInitiativeColumns(
    showScenarii: boolean,
    forcedScenario: KPI.InvestmentScenario = null,
  ): Observable<KPI.CustomColGroupDef[]> {
    return combineLatest([
      this.investmentScenariiService.mainScenario$,
      this.investmentScenariiService.selectedScenarii$.pipe(
        map((scenarii) => scenarii.filter((s) => !!s)),
      ),
      this.buildAgGridActionTagsColumn(false).pipe(first()),
      this.initiativesService.relevantEnergyTypes$,
    ]).pipe(
      map(([mainScenario, selectedScenarii, actionTagsCol, energyTypes]) => {
        const energyTabs = this.config.getSettingValue<string[]>('ENERGY__TABS', []);
        const hasBouquetTravaux = energyTabs.includes('BOUQUET_TRAVAUX');

        const isMainScenarioSelected = mainScenario && selectedScenarii.find((s) =>
          s.id === mainScenario.id
        );
        const isMainScenarioForced = (!!forcedScenario && forcedScenario.id !== mainScenario.id);
        const hideMainScenario = (!!showScenarii && !isMainScenarioSelected) || isMainScenarioForced;
        return [
          {
            headerName: '',
            marryChildren: true,
            headerClass: 'hide-ag-header-label',
            children: [
              {
                field: 'row_nb',
                headerName: '',
                rowDrag: false,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
                resizable: false,
                sortable: false,
                menuTabs: [],
                valueGetter: this.setRowNumber,
                width: 45,
                minWidth: 45,
                maxWidth: 45,
                pinned: true,
                hide: true,
                lockPosition: true,
                lockVisible: true,
                suppressCellFlash: true,
                cellStyle: { 'text-align': 'center', color: '#707171' },
              },
            ]
          },
          // MAIN SCENARIO
          {
            headerName: mainScenario.name,
            pinned: true,
            hide: hideMainScenario,
            suppressColumnsToolPanel: hideMainScenario,
            suppressFiltersToolPanel: hideMainScenario,
            children: [
              {
                field: `initiative_status_scenario_${mainScenario.id}`,
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.initiative_status'),
                width: 130,
                refData: this.investmentStatusesMap,
                availableFilterField: 'initiative_status',
                notNull: true,
                sortable: false,
                lockPosition: true,
                hide: hideMainScenario,
                suppressColumnsToolPanel: hideMainScenario,
                suppressFiltersToolPanel: hideMainScenario,
              },
              {
                field: `savings_start_year_scenario_${mainScenario.id}`,
                headerName: this.translate.instant('Year'),
                width: 100,
                valueFormatter: this.nullFormatter,
                availableFilterField: 'initiative_start_year',
                sortable: true,
                lockPosition: true,
                hide: hideMainScenario,
                suppressColumnsToolPanel: hideMainScenario,
                suppressFiltersToolPanel: hideMainScenario,
              },
            ],
          },
          // FORCED SCENARIO
          ...(
            forcedScenario
              ? [forcedScenario].filter((s) => !s.is_main).map((scenario) => ({
                pinned: true,
                headerName: scenario.name,
                headerClass: `scenario-${scenario.id}`,
                children: [
                  {
                    field: `initiative_status_scenario_${scenario.id}`,
                    headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.initiative_status'),
                    width: 130,
                    refData: this.investmentStatusesMap,
                    cellClass: `scenario-${scenario.id}`,
                    headerClass: `scenario-${scenario.id}`,
                    availableFilterField: 'initiative_status',
                    notNull: true,
                    sortable: false,
                    lockPosition: true,
                  },
                  {
                    field: `savings_start_year_scenario_${scenario.id}`,
                    headerName: this.translate.instant('Year'),
                    width: 100,
                    valueFormatter: this.nullFormatter,
                    cellClass: `scenario-${scenario.id}`,
                    headerClass: `scenario-${scenario.id}`,
                    availableFilterField: 'initiative_start_year',
                    sortable: true,
                    lockPosition: true,
                  },
                ],
              }))
              : []
          ),
          // ALT SCENARII
          ...(
            showScenarii && !forcedScenario
              ? selectedScenarii.filter((scenario: KPI.InvestmentScenario) =>
                !scenario.is_main
              ).map((scenario: KPI.InvestmentScenario) => ({
                pinned: true,
                headerName: scenario.name,
                headerClass: `scenario-${scenario.id}`,
                children: [
                  {
                    field: `initiative_status_scenario_${scenario.id}`,
                    headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.initiative_status'),
                    width: 130,
                    refData: this.investmentStatusesMap,
                    cellClass: `scenario-${scenario.id}`,
                    headerClass: `scenario-${scenario.id}`,
                    availableFilterField: 'initiative_status',
                    notNull: true,
                    sortable: false,
                    lockPosition: true,
                  },
                  {
                    field: `savings_start_year_scenario_${scenario.id}`,
                    headerName: this.translate.instant('Year'),
                    width: 100,
                    valueFormatter: this.nullFormatter,
                    cellClass: `scenario-${scenario.id}`,
                    headerClass: `scenario-${scenario.id}`,
                    availableFilterField: 'initiative_start_year',
                    sortable: true,
                    lockPosition: true,
                  },
                ],
              }))
              : []
          ),
          {
            headerName: this.translate.instant("Desired gains"),
            headerClass: 'justify-center',
            hide: false,
            groupId: "desired_gains",
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_desired_gain',
                headerName: '',
                width: 85,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed' as const,
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: `gain_all`,
                headerName: this.translate.instant("All energy"),
                valueFormatter: this.kwhFormatter,
                cellRenderer: 'evolutionRenderer',
                cellClass: this.stoppedTrajectoryCellClass(forcedScenario, mainScenario),
                hide: false,
                width: 150,
                menuTabs: [],
                minWidth: 100,
                filter: 'agNumberColumnFilter',
                columnGroupShow: 'open' as ColumnGroupShowType,
              } as KPI.CustomColDef,
              ...energyTypes.map((energyType): KPI.CustomColDef => ({
                field: `gain_${energyType.id}`,
                headerName: energyType.name,
                hide: !energyType.is_electricity,
                width: Math.min(
                  Math.max((energyType.name.length * 8 + 60), 140),
                  240,
                ),
                valueFormatter: this.kwhFormatter,
                cellRenderer: 'evolutionRenderer',
                cellClass: this.stoppedTrajectoryCellClass(forcedScenario, mainScenario),
                filter: 'agNumberColumnFilter',
                menuTabs: [],
                columnGroupShow: 'open' as ColumnGroupShowType,
                headerClass: `energy-type-${energyType.id}`,
                minWidth: 100,
              })),
              // CO2 SAVINGS
              {
                field: 'greenhouse_gas_savings_full_year',
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.GHG_emissions_avoided_first_year')
                  + ' (i)',
                valueFormatter: this.ghgFormatter,
                cellClass: this.stoppedTrajectoryCellClass(forcedScenario, mainScenario),
                width: 130,
                filter: false,
                sortable:false,
                // @ts-ignore
                menuLabel: this.translate.instant('pages.common.ag-grid.menu_labels.GHG_emissions_avoided_first_year'),
                menuDependsOnKWhSavings: true,
                headerTooltip: this.translate.instant('pages.energy.initiative.labels.ghg_savings_first_year'),
                hide: true,
                minWidth: 100,
                columnGroupShow: 'open' as ColumnGroupShowType,
              } as KPI.CustomColDef,
            ]
          } as KPI.CustomColGroupDef,
          // FIDJI ACT SHORT LABEL
          {
            headerName: this.translate.instant('Fidji ACT short label'),
            headerClass: 'hide-ag-header-label',
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'initiative_type_3',
                headerName: this.translate.instant('Fidji ACT short label'),
                width: 150,
                refData: this.initiativeTypesLeafFriendlyMap,
                hide: false,
                minWidth: 200,
                valueFormatter: (params) => {
                  if (params.value == null || params.value === '') {
                    return '';
                  }
                  return this.initiativeTypesLeafFriendlyMap[params.value] || '';
                },
              },
            ]
          },
          // Main information
          {
            headerName: this.translate.instant(
              'pages.common.ag-grid.column_groups.investment_main',
            ),
            headerClass: 'd-none',
            children: [
              {
                field: 'initiative_id',
                headerName: '',
                menuTabs: [],
                hide: true,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                lockPosition: true,
                minWidth: 100,
              },
              // LABEL
              {
                field: 'initiative_label',
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.initiative_label'),
                pinned: true,
                lockPosition: true,
                width: 450,
                minWidth: 250,
                valueFormatter: this.nullFormatter,
                // @ts-ignore
                menuLabel: this.translate.instant('pages.common.ag-grid.menu_labels.initiative_label'),
              },
              ...(hasBouquetTravaux ? [{
                field: 'bouquet',
                headerName: this.translate.instant('Bouquet'),
                pinned: true,
                lockPosition: true,
                width: 100,
                minWidth: 100,
                // @ts-ignore
                menuLabel: this.translate.instant('Bouquet'),
              }] : []),
              // INVESTMENT BUDGET
              {
                field: 'related_invest_budget',
                headerName: this.translate.instant('Budget'),
                width: 100,
                sortable: true,
                valueFormatter: this.priceFormatter,
                filter: 'agNumberColumnFilter',
                minWidth: 100,
              },
              // IS CAPEX
              {
                field: 'related_invest_is_capex',
                headerName: this.translate.instant('CAPEX'),
                width: 100,
                sortable: true,
                refData: this.trueFalseMap,
                minWidth: 100,
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
              },
              // BUDGET ORIGIN
              {
                field: 'related_invest_budget_origin',
                headerName: this.translate.instant('Budget origin'),
                width: 100,
                menuLabel: this.translate.instant('Budget origin'),
                filter: 'agSetColumnFilter',
                minWidth: 150,
                refData: this.budgetOriginsMap,
                filterParams: {
                  values: (params) => {
                    if (params && params.colDef && params.colDef.field) {
                      this.subscriptions.push(
                        combineLatest([
                          this.perimetersService.perimeters$,
                          this.yearsService.startYear$,
                          this.yearsService.endYear$,
                        ])
                          .pipe(
                            switchMap(([perimeters, startYear, endYear]) => {
                              const { api } = params;
                              return this.api.available_filter_values({
                                model: params.colDef.availableFilterField || params.colDef.field,
                                buildings: PerimetersService.getBuildingsIds(perimeters),
                                filterModel: api.getFilterModel(),
                                start_year: startYear,
                                end_year: endYear,
                                table: 'Initiative',
                              });
                            }),
                          )
                          .subscribe((res) => params.success(res)),
                      );
                    }
                  },
                  valueFormatter: (params) => {
                    if (params.value === null || params.value === undefined || params.value === '') {
                      return this.translate.instant('pages.common.ag-grid.internal.blanks');
                    }
                    return this.budgetOriginsMap[params.value] || params.value;
                  },
                },
                valueFormatter: (params) => {
                  if (params.value === null || params.value === undefined || params.value === '') {
                    return '-';
                  }
                  return this.budgetOriginsMap[params.value] || params.value;
                },
              },
              {
                field: 'custom_code',
                headerName: this.investmentCodeFieldName || this.translate.instant('Customize code'),
                width: 100,
                filter: 'agSetColumnFilter',
                minWidth: 150,
              },
              {
                field: 'quantity',
                headerName: this.translate.instant('Qty'),
                width: 80,
                filter: 'agNumberColumnFilter',
                minWidth: 100,
              },
              {
                field: 'quantity_unit',
                headerName: this.translate.instant('Qty unit'),
                filter: 'agSetColumnFilter',
                width: 80,
                hide: false,
                minWidth: 100,
                filterParams: {
                  values: (params) => {
                    if (params && params.colDef && params.colDef.field) {
                      this.subscriptions.push(
                        combineLatest([
                          this.perimetersService.perimeters$,
                          this.yearsService.startYear$,
                          this.yearsService.endYear$,
                        ])
                          .pipe(
                            switchMap(([perimeters, startYear, endYear]) => {
                              const { api } = params;
                              return this.api.available_filter_values({
                                model: 'QuantityUnit',
                                buildings: PerimetersService.getBuildingsIds(perimeters),
                                filterModel: api.getFilterModel(),
                                start_year: startYear,
                                end_year: endYear,
                                table: 'Initiative',
                              });
                            }),
                          )
                          .subscribe((res) => {
                            params.success(res);
                          }),
                      );
                    }
                  },
                },
                valueFormatter: (params) => {
                  return params.value;
                },
              },
              // BUDGET SCOPES
              {
                field: 'related_invest_budget_scopes',
                headerName: this.translate.instant('Budget scope'),
                width: 150,
                sortable: false,
                hide: false,
                cellRenderer: (params) => {
                  const result = this.budgetScopesFormatter(params);
                  return result;
                },
                filter: 'agSetColumnFilter',
                minWidth: 100,
                availableFilterField: 'BudgetScope',
                filterParams: {
                  values: (params) => {
                    if (params && params.colDef && params.colDef.field) {
                      this.subscriptions.push(
                        combineLatest([
                          this.perimetersService.perimeters$,
                          this.yearsService.startYear$,
                          this.yearsService.endYear$,
                        ])
                          .pipe(
                            switchMap(([perimeters, startYear, endYear]) => {
                              const { api } = params;
                              return this.api.available_filter_values({
                                model: params.colDef.availableFilterField || params.colDef.field,
                                buildings: PerimetersService.getBuildingsIds(perimeters),
                                filterModel: api.getFilterModel(),
                                start_year: startYear,
                                end_year: endYear,
                                table: 'Initiative',
                              });
                            }),
                            map((res) => {
                              return res.map(item => {
                                if (typeof item === 'object' && item.id) {
                                  return item.id;
                                }
                                return item;
                              });
                            })
                          )
                          .subscribe((res) => {
                            params.success(res);
                          }),
                      );
                    }
                  },
                },
              },
              // INVESTMENT CODE
              {
                field: 'code',
                headerName: this.translate.instant('Action code'),
                width: 110,
                filter: 'agSetColumnFilter',
                minWidth: 100,
                availableFilterField: 'ActionCode',
                filterParams: {
                  values: (params) => {
                    if (params && params.colDef && params.colDef.field) {
                      this.subscriptions.push(
                        combineLatest([
                          this.perimetersService.perimeters$,
                          this.yearsService.startYear$,
                          this.yearsService.endYear$,
                        ])
                          .pipe(
                            switchMap(([perimeters, startYear, endYear]) => {
                              const { api } = params;
                              return this.api.available_filter_values({
                                model: params.colDef.availableFilterField || params.colDef.field,
                                buildings: PerimetersService.getBuildingsIds(perimeters),
                                filterModel: api.getFilterModel(),
                                start_year: startYear,
                                end_year: endYear,
                                table: 'Initiative',
                              });
                            }),
                          )
                          .subscribe((res) => {
                            params.success(res);
                          }),
                      );
                    }
                  },
                },
              },
              // SITE
              {
                field: 'site',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_site'),
                hide: false,
                width: 110,
                sort: 'asc',
                refData: this.sitesMap,
                quickFilter: true,
                minWidth: 100,
                filterParams: {
                  values: (params) => {
                    // supply values to the set filter
                    this.perimetersService.perimeters$.subscribe((perimeters) =>
                      params.success([
                        ...new Set(
                          perimeters
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((p) => p.id),
                        ),
                      ]),
                    );
                  },
                },
              },
              // PERIMETER
              {
                field: 'perimeter',
                headerName: this.translate.instant('Perimeter name'),
                width: 200,
                hide: false,
                refData: this.perimetersMap,
                minWidth: 150,
                filterParams: {
                  values: (params) => {
                    // supply values to the set filter
                    this.perimetersService.perimeters$.subscribe((perimeters) =>
                      params.success([
                        ...new Set(
                          perimeters
                            .reduce((acc: KPI.Perimeter[], p) => {
                              acc.push(...p.sub_perimeters.map((sp) => ({
                                ...sp,
                                parent: p,
                              })));
                              return acc;
                            }, [])
                            .sort((a, b) => a.parent?.name.localeCompare(b.parent?.name))
                            .map((p) => p.id),
                        ),
                      ]),
                    );
                  },
                },
              },
              {
                field: 'perimeter_type',
                headerName: this.translate.instant('Child perimeter type'),
                hide: false,
                refData: this.perimeterTypesMap,
                notNull: true,
                minWidth: 100,
              },
               // INITIATIVE CATEGORY
              {
                field: 'initiative_type_1',
                headerName: this.translate.instant('Fidji ACT family'),
                width: 150,
                hide: true,
                refData: this.initiativeTypesMap,
                minWidth: 100,
              },
              // ACTION LONG LABEL
              {
                field: 'initiative_type_2',
                headerName: this.translate.instant('Fidji ACT long label'),
                width: 150,
                refData: this.initiativeTypesMap,
                hide: true,
                minWidth: 200,
              },
              // CONFIDENCE ON FEASIBILITY
              {
                field: 'confidence_on_feasibility',
                headerName: this.translate.instant('Confidence on feasibility'),
                width: 150,
                refData: this.confidenceOnFeasibilityMap,
                quickFilter: true,
                hide: true,
                minWidth: 100,
              },
              // CREATED BY
              {
                field: 'initially_proposed_by',
                headerName: this.translate.instant('Initially proposed by'),
                width: 160,
                sortable: false,
                hide: true,
                minWidth: 100,
              },
              // INITIATIVE RESPONSIBLE
              {
                field: 'responsible',
                headerName: this.translate.instant('Initiative owner'),
                width: 140,
                sortable: true,
                filter: true,
                quickFilter: true,
                hide: true,
                minWidth: 100,
              },
              // INVESTMENT / KWH RATIO
              {
                field: 'invest_kwh_ratio',
                headerName: this.translate.instant('Ratio {currency}/kWh saved', { currency: this.i18n.getCurrency() }),
                valueFormatter: this.priceFormatter2dec,
                width: 160,
                hide: true,
                filter: 'agNumberColumnFilter',
                minWidth: 100,
              },
              // CEE BONUS
              {
                field: 'cee_bonus',
                headerName: this.translate.instant('CEE bonus'),
                width: 130,
                hide: true,
                filter: 'agNumberColumnFilter',
                valueFormatter: this.priceFormatter,
                minWidth: 100,
              },
              // OTHER BONUSES
              {
                field: 'yearly_cost_savings',
                headerName: this.translate.instant('Other bonuses'),
                width: 130,
                hide: true,
                filter: 'agNumberColumnFilter',
                valueFormatter: this.priceFormatter,
                minWidth: 100,
              },
              // INVESTMENT
              {
                field: 'related_invest',
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.related_invest'),
                width: 160,
                menuTabs: [],
                sortable: false,
                hide: true,
                valueFormatter: this.nullFormatter,
                minWidth: 100,
              },
              // DESCRIPTION
              {
                field: 'description',
                headerName: this.translate.instant('Action description'),
                width: 150,
                menuTabs: [],
                sortable: false,
                hide: true,
                valueFormatter: this.nullFormatter,
                minWidth: 100,
              },
              // KWH SAVINGS FIRST YEAR
              {
                field: 'theoretical_savings_first_year',
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.theoretical_savings_first_year'),
                valueFormatter: this.kwhFormatter,
                width: 150,
                filter: 'agNumberColumnFilter',
                hide: true,
                // @ts-ignore
                menuLabel: this.translate.instant('pages.common.ag-grid.menu_labels.theoretical_savings_first_year'),
                minWidth: 100,
              },
              // INVESTMENT STATUSES
              {
                field: 'investment_statuses',
                headerName: '',
                menuTabs: [],
                sortable: false,
                hide: true,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                minWidth: 100,
              },
              // PAYBACK
              // {
              //   field: 'payback',
              //   headerName: this.translate.instant('Payback'),
              //   hide: true,
              //   width: 110,
              //   filter: 'agNumberColumnFilter',
              //   valueFormatter: this.paybackFormatter,
              //   minWidth: 100,
              // },
            ],
          },
          // TAGS
          {
            headerName: this.translate.instant(
              'pages.common.ag-grid.column_groups.tag',
            ),
            children: (actionTagsCol as KPI.CustomColDef[]),
          },
        ];
      })
    );
  }

  private buildAgGridActionTagsColumn(hide: boolean = false): Observable<KPI.CustomColDef[]> {
    return this.initiativeTagsService.initiativeTags$.pipe(
      first(),
      map((initiativeTags) => {
        return initiativeTags.reduce(
          (columns, initiativeTag) =>
            columns.concat([
              {
                field: `action_tag_${initiativeTag.id}`,
                headerName: initiativeTag.name,
                hide,
                width: 125,
                cellStyle: params => {
                  if (params.value === true) {
                    return { color: '#00a000', fontWeight: 'bold' };
                  }
                },
                refData: this.trueFalseMap,
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
                minWidth: 100,
              },
            ]),
          [],
        );
      }),
    );
  }

  public getAgGridInitiativeColumns(
    showScenarii: boolean,
    forcedScenario: KPI.InvestmentScenario = null,
  ): Observable<any[]> {
    return combineLatest([
      this.agGridInitiativeColumns(showScenarii, forcedScenario),
      combineLatest([
        this.scope.investmentStatuses$.pipe(first(), idMapToArray()),
        this.initiativesService.initiativeTypes$,
        this.initiativesService.initiativeEnergyTypes$,
        this.initiativesService.initiativeOwners$,
        this.scope.investmentBudgetOrigins$.pipe(first(), idMapToArray()),
      ]),
      this.scope.confidencesOnFeasibility$.pipe(first(), idMapToArray()),
      this.initiativesService.emissionCategories$,
      this.scope.perimeterTypes$.pipe(idMapToArray()),
      this.scope.budgetScopes$.pipe(first(), idMapToArray()),
      this.quantityUnitsService.quantityUnits$.pipe(first()),
      this.loadSiteAndPerimeters(),
    ]).pipe(
      map(
        ([
          columns,
          [
            statuses,
            initiativeTypes,
            energyTypes,
            initiativeOwners,
            budgetOrigins,
          ],
          confidencesOnFeasibility,
          emissionCategories,
          perimeterTypes,
          budgetScopes,
          units,
        ]) => {
          /**
           * Setup reference data
           */
          statuses.forEach((investmentStatus) => {
            this.investmentStatusesMap[investmentStatus.id] =
              investmentStatus.name;
          });
          initiativeTypes.forEach((initiativeType) => {
            this.initiativeTypesMap[initiativeType.id] =
              initiativeType.name;
            this.initiativeTypesFriendlyNameMap[initiativeType.id] =
              initiativeType.friendly_name || '';
            if (initiativeType.parent !== null) {
              this.initiativeTypesLeafFriendlyMap[initiativeType.id] = initiativeType.friendly_name || initiativeType.name;
            }
          });
          initiativeTypes.forEach((initiativeType) => {
            this.initiativeTypesShortMap[initiativeType.id] =
              initiativeType.setup_reference;
          });
          energyTypes.forEach((energyType) => {
            this.energyTypesMap[energyType.id] =
              energyType.name;
          });
          emissionCategories.forEach((emissionCategory) => {
            this.emissionCategoriesMap[emissionCategory.id] =
              emissionCategory.name;
          });
          initiativeOwners.forEach((initiativeOwner) => {
            this.initiativeOwnersMap[initiativeOwner.id] =
              initiativeOwner.name;
          });
          confidencesOnFeasibility.forEach((confidenceOnFeasibility) => {
            this.confidenceOnFeasibilityMap[confidenceOnFeasibility.id] =
              confidenceOnFeasibility.name;
          });
          perimeterTypes.forEach((perimeterType) => {
            this.perimeterTypesMap[perimeterType.id] = perimeterType.name;
          });
          budgetScopes.forEach((budgetScope) => {
            this.budgetScopesMap[budgetScope.id] = budgetScope.name;
          });
          budgetOrigins.forEach((budgetOrigin) => {
            this.budgetOriginsMap[budgetOrigin.id] = budgetOrigin.name;
          });
          this.quantityUnitMap = { null: units.find((u) => u.setup_reference === 'no_unit').name };
          (units as KPI.QuantityUnit[]).forEach((unit) => {
            this.quantityUnitMap[unit.name] = unit.name;
          });

          const agGridInitiativeColumns = columns;

          return agGridInitiativeColumns;
        },
      ),
    );
  }

  public getFidjiActInitiativeColumns(): Observable<any[]> {
    return combineLatest([
      this.agGridInitiativeColumns(false),
      this.perimetersService.allPerimeters$,
      this.initiativesService.initiativeTypes$,
      this.initiativesService.initiativeOwners$,
      this.scope.confidencesOnFeasibility$.pipe(first(), idMapToArray()),
      this.scope.budgetScopes$.pipe(first(), idMapToArray()),
      this.investmentScenariiService.mainScenario$,
      this.scope.investmentStatuses$.pipe(first(), idMapToArray()),
    ]).pipe(
      map(
        ([
          columns,
          perimeters,
          initiativeTypes,
          initiativeOwners,
          confidencesOnFeasibility,
          budgetScopes,
          scenario,
          statuses,
        ]) => {
          /**
           * Setup reference data
           */
          perimeters.forEach((perimeter) => {
            this.sitesMap[perimeter.id] = perimeter.name;
            perimeter.sub_perimeters.forEach((sub_perimeter) => {
              this.perimetersMap[sub_perimeter.id] = sub_perimeter.name;
              this.perimeters[sub_perimeter.id] = sub_perimeter.name;
            });
          });
          initiativeTypes.forEach((initiativeType) => {
            this.initiativeTypesMap[initiativeType.id] =
              initiativeType.name;
            this.initiativeTypesFriendlyNameMap[initiativeType.id] =
              initiativeType.friendly_name || '';
            if (initiativeType.parent !== null) {
              this.initiativeTypesLeafFriendlyMap[initiativeType.id] = initiativeType.friendly_name || initiativeType.name;
            }
          });
          initiativeTypes.forEach((initiativeType) => {
            this.initiativeTypesShortMap[initiativeType.id] =
              initiativeType.setup_reference;
          });
          initiativeOwners.forEach((initiativeOwner) => {
            this.initiativeOwnersMap[initiativeOwner.id] =
              initiativeOwner.name;
          });
          confidencesOnFeasibility.forEach((confidenceOnFeasibility) => {
            this.confidenceOnFeasibilityMap[confidenceOnFeasibility.id] =
              confidenceOnFeasibility.name;
          });
          budgetScopes.forEach((budgetScope) => {
            this.budgetScopesMap[budgetScope.id] = budgetScope.name;
          });
          statuses.forEach((investmentStatus) => {
            this.investmentStatusesMap[investmentStatus.id] =
              investmentStatus.name;
          });

          const agGridInitiativeColumns = columns.reduce((acc, column) => {
            if ((column as KPI.CustomColGroupDef).children?.length) {
              acc.push(...(column as KPI.CustomColGroupDef).children);
            } else {
              acc.push(column);
            }
            return acc;
          }, []);

          const order = [
            'row_nb',
            'site',
            'perimeter',
            'initiative_label',
            'bouquet',
            `savings_start_year_scenario_${scenario.id}`,
            `initiative_status_scenario_${scenario.id}`,
            'related_invest_budget',
            'related_invest_budget_scopes',
            'related_invest_budget_origin',
            // COST FIABILITY
            // COST COMMENT
            'gain_all',
            'initiative_type_3',
            'responsible',
          ];
          const customColumnNames = {
            'gain_all': this.translate.instant('Total kWh gain'),
            'related_invest_budget': this.translate.instant('Cost'),
            'related_invest_budget_scopes': this.translate.instant('Budget scope'),
          };
          const filteredColumns = agGridInitiativeColumns.filter((column) => order.includes((column as any).field));
          const sortedColumns = filteredColumns.sort((a, b) => order.indexOf((a as any).field) - order.indexOf((b as any).field));

          return sortedColumns
            .concat([{
              field: 'edit',
              headerName: '',
              filter: false,
              cellRenderer: SeeButtonRendererComponent,
              cellRendererParams: {
                callback: (initiativeId) => {
                  this.initiativesService.getInitiative(initiativeId).pipe(first()).subscribe((initiative) => {
                    this.rightPanelDialogService.open(
                      InitiativeFormComponent,
                      {
                        autoFocus: false,
                        closeOnBackdropClick: false,
                        context: {
                          initiative,
                        }
                      }
                    )
                  });
                },
                getReturnedValue: (params) => params.data?.initiative_id,
              },
            }])
            .map((column) => ({
              ...column,
              hide: false,
              pinned: false,
              lockPosition: true,
              flex: ['site', 'perimeter', 'initiative_label', 'bouquet'].includes(column.field) ? 1 : null,
              minWidth: ['site', 'perimeter', 'initiative_label'].includes(column.field) ? 150 : null,
              headerName: customColumnNames[column.field] || column.headerName,
            }));
        },
      ),
    );
  }

  public getInitiativesData(options: any): Observable<any> {
    if (options.filterModel?.related_invest_budget_origin?.values) {
      options.filterModel.related_invest_budget_origin.values =
        options.filterModel.related_invest_budget_origin.values.map((value) =>
          value !== null ? Number(value) : null
        );
    }

    if (options.filterModel?.related_invest_budget_scopes?.values) {
      const budgetScopeValues = options.filterModel.related_invest_budget_scopes.values;
      const convertedValues = budgetScopeValues.map(value => {
        const scopeId = Object.keys(this.budgetScopesMap).find(id =>
          this.budgetScopesMap[id] === value
        );
        return scopeId ? parseInt(scopeId) : value;
      });
      options.filterModel.related_invest_budget_scopes.values = convertedValues;
    }

    return combineLatest([
      this.api.initiatives_table(options),
      this.investmentScenariiService.scenarii$,
      this.initiativeTagsService.initiativeTags$
    ])
      .pipe(
        tap(() => this.currentInitiativeTableOptions.next(options)),
        map(([data, scenarii, initiativeTags]) => ({
          ...data,

          rowData: data.rowData.map((row) => {
            const newRow = {
              ...row,
            }

            // TAGS
            initiativeTags.map((actionTag) => {
              newRow[`action_tag_${actionTag.id}`] = !!row.initiative_tags?.find((tagId) => tagId === actionTag.id);
            });

            // STATUS & YEAR BY SCENARIO
            scenarii.map((scenario) => {
              const scenarioInstance = row.instances.find((instance) =>
                instance.scenario === scenario.id,
              );
              newRow[`initiative_status_scenario_${scenario.id}`] = scenarioInstance?.status || null;
              newRow[`savings_start_year_scenario_${scenario.id}`] = scenarioInstance?.savings_start_year || null;
              newRow[`savings_start_at_scenario_${scenario.id}`] = scenarioInstance?.savings_start_at || null;
              newRow[`instance_id_scenario_${scenario.id}`] = scenarioInstance?.id || null;
              newRow[`stopped_trajectory_effects_scenario_${scenario.id}`] = scenarioInstance?.stopped_trajectory_effects || false;
            });

            // GAINS
            newRow['gain_all'] = row.gains.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0);
            newRow['theoretical_savings_first_year'] = row.gains.reduce((acc, gain) => acc + (gain.theoretical_savings_first_year || 0), 0) || null;
            row.gains.map((gain) => {
              newRow[`gain_${gain.energy_type_id}`] = gain.theoretical_savings_full_year || 0;
            });

            return newRow;
          }),
        })),
      );
  }

  /**
   * Builds status filter for initiative table, to be sent through URL
   *
   * @param statusAttributes: KPI.InitiativeStatusAttribute[] - List of status sttributes to filter on
   * @param value: boolean - Value of the status attributes to filter on
   * @returns string
   *
   * Example: buildStatusFilter(['hypothesis'], true)
   *          => '1,2'
   */
  public buildStatusFilter(
    statusAttributes: KPI.InitiativeStatusAttribute[],
    value: boolean
  ): Observable<number[]> {
    return this.initiativesService.initiativeStatuses$.pipe(
      map((statuses) =>
        statuses.filter((status) =>
          statusAttributes.some((attr) => status[attr] === value),
        ).map((status) => status.id),
      ),
    );
  }

  public get plannedStatusFilter$(): Observable<number[]> {
    return this.buildStatusFilter(['is_planned', 'completed', 'in_progress'], true);
  }

  public get hypothesisStatusFilter$(): Observable<number[]> {
    return this.buildStatusFilter(['hypothesis'], true);
  }

  public preExportInitiatives(columnDefs: ColDef<any, any>[]) {
    return this.api.pre_download_initiatives_excel_export({ columnDefs, payload: this.currentInitiativeTableOptions.value })
  }

  public exportInitiatives(columnDefs: ColDef<any, any>[]) {
    return this.exportsService.download_initiatives_excel_export({ columnDefs, payload: this.currentInitiativeTableOptions.value })
  }

  public budgetScopesFormatter(params) {
    if (!params.value || !Array.isArray(params.value) || params.value.length === 0) {
      return '-';
    }

    const uniqueScopeIds = [...new Set(params.value as number[])];
    const scopeNames = uniqueScopeIds
      .map(scopeId => {
        const scopeName = this.budgetScopesMap[scopeId] || scopeId;
        return scopeName;
      })
      .filter(name => name && name !== null);

    let result = '-';
    if (scopeNames.length === 1) {
      result = scopeNames[0] as string;
    } else if (scopeNames.length === 2) {
      result = scopeNames.join(' & ');
    } else if (scopeNames.length >= 3) {
      result = `${scopeNames.slice(0, -1).join(', ')} & ${scopeNames.slice(-1)}`;
    }

    return result;
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
